import Vue from 'vue'
import Vuex, { Module } from 'vuex'
import axios from 'axios'
import { notification } from 'ant-design-vue'
import KAMFormModel from '@/models/KAMFormModel'
import TableSettings from '@/types/tableSettings'
import { KAMForm, KAMFormList } from '@/types/kam'
import { ClosedKAMFormStatuses, OpenKAMFormStatuses } from '@/helpers/kamFormStates'
import {
  ensureAnswersMedia,
  ensureAnswersObject,
  PendingUploadsMap,
  removeTemporaryMedia,
  sanitizeAnswersForSave
} from '@/helpers/kamFormUtils'

Vue.use(Vuex)

type KAMFormWithUploads = KAMForm & { _pendingUploads?: PendingUploadsMap }

function initializeKAMForm (form: KAMFormWithUploads|null): KAMFormWithUploads|null {
  if (!form) return null
  form.answers = ensureAnswersObject(form.answers)
  ensureAnswersMedia(form)
  return form
}

export const kamStore: Module<any, any> = {
  state: {
    kamFormList: {
      data: [],
      meta: {
        per_page: 25,
        total: 0
      },
      filters: {}
    } as KAMFormList,
    currentKAMForm: null as KAMFormWithUploads|null,
    loadingKAMForm: {
      list: false,
      item: false,
      save: false
    }
  },
  mutations: {
    setKAMFormList (state, kamFormList: KAMFormList) {
      state.kamFormList = kamFormList
    },
    setCurrentKAMForm (state, kamForm: KAMFormWithUploads|null) {
      state.currentKAMForm = kamForm
    },
    setLoadingKAMForm (state, data: { type: string, state: boolean }) {
      state.loadingKAMForm[data.type] = data.state
    }
  },
  actions: {
    getKAMFormList ({ commit }, tableSettings: TableSettings) {
      commit('setLoadingKAMForm', { type: 'list', state: true })
      let kamFormModel = new KAMFormModel()

      if (tableSettings.activeFilters) {
        Object.keys(tableSettings.activeFilters).forEach(key => {
          if (tableSettings.activeFilters[key] && tableSettings.activeFilters[key].length) {
            kamFormModel = kamFormModel.whereIn(key, tableSettings.activeFilters[key])
          }
        })
      }

      if (tableSettings.archived) {
        kamFormModel = kamFormModel.whereIn('status', tableSettings.activeFilters?.status || ClosedKAMFormStatuses)
      } else {
        kamFormModel = kamFormModel.whereIn('status', tableSettings.activeFilters?.status || OpenKAMFormStatuses)
      }

      if (tableSettings.search) {
        kamFormModel = kamFormModel.where('search', tableSettings.search)
      }

      if (tableSettings.sort && tableSettings.sort.order && tableSettings.sort.columnKey) {
        kamFormModel = kamFormModel.orderBy(tableSettings.sort.order === 'ascend' ? tableSettings.sort.columnKey : '-' + tableSettings.sort.columnKey)
      }

      if (tableSettings.pagination) {
        kamFormModel = kamFormModel.limit(tableSettings.pagination.pageSize).page(tableSettings.pagination.current)
      }

      kamFormModel.get().then((kamFormList) => {
        commit('setKAMFormList', kamFormList)
      }).catch(() => {
        notification.error({ message: 'Fout tijdens het ophalen van de KAM-formulieren!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.', duration: 3 })
      }).finally(() => {
        commit('setLoadingKAMForm', { type: 'list', state: false })
      })
    },
    loadKAMFormById ({ commit }, id: number|string): Promise<KAMFormWithUploads> {
      commit('setLoadingKAMForm', { type: 'item', state: true })
      return axios.get(process.env.VUE_APP_API_URL + '/kamForms/' + id).then((response) => {
        const form = initializeKAMForm(response.data.data)
        if (!form) throw new Error('KAM form could not be initialized')
        commit('setCurrentKAMForm', form)
        return form
      }).catch((error) => {
        notification.error({ message: 'Fout tijdens het ophalen van dit KAM-formulier!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.', duration: 3 })
        throw error
      }).finally(() => {
        commit('setLoadingKAMForm', { type: 'item', state: false })
      })
    },
    clearCurrentKAMForm ({ commit }) {
      commit('setCurrentKAMForm', null)
    },
    async saveKAMForm ({ commit, state, dispatch }, data: { id: number|string, answers: Record<string, any>, status?: number }): Promise<KAMFormWithUploads> {
      commit('setLoadingKAMForm', { type: 'save', state: true })

      const payload: Record<string, any> = {
        answers: sanitizeAnswersForSave(data.answers)
      }
      if (typeof data.status !== 'undefined') payload.status = data.status

      try {
        const response = await axios.put(process.env.VUE_APP_API_URL + '/kamForms/' + data.id, payload)
        const form = initializeKAMForm(response.data.data)
        if (!form) throw new Error('KAM form could not be initialized')
        if (state.currentKAMForm?._pendingUploads) {
          form._pendingUploads = state.currentKAMForm._pendingUploads
        }
        commit('setCurrentKAMForm', form)
        await dispatch('processKAMFormPendingUploads', data.id)
        return form
      } catch (error) {
        notification.error({ message: 'Fout bij opslaan', description: 'Er is een fout opgetreden bij het opslaan van het formulier.' })
        throw error
      } finally {
        commit('setLoadingKAMForm', { type: 'save', state: false })
      }
    },
    async processKAMFormPendingUploads ({ state, commit }, id: number|string): Promise<void> {
      const form = state.currentKAMForm as KAMFormWithUploads|null
      if (!form?._pendingUploads) return

      const pending = form._pendingUploads

      for (const key of Object.keys(pending)) {
        for (const item of pending[key]) {
          if (!item.file) continue
          const formData = new FormData()
          formData.append('position', key)
          formData.append('files', item.file)
          const response = await axios.post(process.env.VUE_APP_API_URL + '/kamForms/' + id + '/media', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          const updated = initializeKAMForm(response.data.data)
          if (updated) commit('setCurrentKAMForm', updated)
        }
      }
    },
    async deleteKAMFormMedia ({ state, commit }, data: { formId: number|string, fileId: number|string }): Promise<void> {
      const form = state.currentKAMForm as KAMFormWithUploads|null
      if (!form) return

      if (typeof data.fileId === 'string' && data.fileId.startsWith('tmp-')) {
        removeTemporaryMedia(form, data.fileId)
        commit('setCurrentKAMForm', form)
        return
      }

      const response = await axios.delete(process.env.VUE_APP_API_URL + '/kamForms/' + data.formId + '/media/' + data.fileId)
      commit('setCurrentKAMForm', initializeKAMForm(response.data.data))
    },
    async reportKAMForm ({ commit }, data: { formId: number|string, managerId: number|string|null }): Promise<void> {
      const response = await axios.patch(process.env.VUE_APP_API_URL + '/kamForms/' + data.formId + '/report', { manager_id: data.managerId })
      commit('setCurrentKAMForm', initializeKAMForm(response.data.data))
    }
  },
  getters: {
    kamFormList: state => state.kamFormList,
    currentKAMForm: state => state.currentKAMForm,
    loadingKAMForm: state => state.loadingKAMForm
  }
}
